import React from 'react';
import SnippetArea from './SnippetArea/SnippetArea';

// Define types object outside component to avoid recreation on each render
const TYPES = {
  Array: {
    mainColor: 'bg-gradient-to-br from-blue-500 to-cyan-600',
    accentColor: 'bg-blue-500',
    textColor: 'text-blue-50',
    badgeBg: 'bg-blue-200',
    badgeText: 'text-blue-800',
    lightBg: 'bg-blue-50',
    borderColor: 'border-blue-300',
    icon: (
      <span className="font-['Font_Awesome_5_Free'] text-lg text-white">
        &#xf0ce;
      </span>
    )
  },
  Boolean: {
    mainColor: 'bg-gradient-to-br from-green-500 to-emerald-600',
    accentColor: 'bg-green-500',
    textColor: 'text-green-50',
    badgeBg: 'bg-green-200',
    badgeText: 'text-green-800',
    lightBg: 'bg-green-50',
    borderColor: 'border-green-300',
    icon: (
      <span className="font-['Font_Awesome_5_Free'] text-lg text-white">
        &#xf058;
      </span>
    )
  },
  Date: {
    mainColor: 'bg-gradient-to-br from-purple-500 to-violet-600',
    accentColor: 'bg-purple-500',
    textColor: 'text-purple-50',
    badgeBg: 'bg-purple-200',
    badgeText: 'text-purple-800',
    lightBg: 'bg-purple-50',
    borderColor: 'border-purple-300',
    icon: (
      <span className="font-['Font_Awesome_5_Free'] text-lg text-white">
        &#xf133;
      </span>
    )
  },
  Number: {
    mainColor: 'bg-gradient-to-br from-red-500 to-rose-600',
    accentColor: 'bg-red-500',
    textColor: 'text-red-50',
    badgeBg: 'bg-red-200',
    badgeText: 'text-red-800',
    lightBg: 'bg-red-50',
    borderColor: 'border-red-300',
    icon: (
      <span className="font-['Font_Awesome_5_Free'] text-lg text-white">
        &#xf292;
      </span>
    )
  },
  Object: {
    mainColor: 'bg-gradient-to-br from-amber-500 to-orange-600',
    accentColor: 'bg-amber-500',
    textColor: 'text-amber-50',
    badgeBg: 'bg-amber-200',
    badgeText: 'text-amber-800',
    lightBg: 'bg-amber-50',
    borderColor: 'border-amber-300',
    icon: (
      <span className="font-['Font_Awesome_5_Free'] text-lg text-white">
        &#xf542;
      </span>
    )
  },
  String: {
    mainColor: 'bg-gradient-to-br from-indigo-500 to-blue-600',
    accentColor: 'bg-indigo-500',
    textColor: 'text-indigo-50',
    badgeBg: 'bg-indigo-200',
    badgeText: 'text-indigo-800',
    lightBg: 'bg-indigo-50',
    borderColor: 'border-indigo-300',
    icon: (
      <span className="font-['Font_Awesome_5_Free'] text-lg text-white">
        &#xf031;
      </span>
    )
  }
};

// Default type details for unknown types
const DEFAULT_TYPE = {
  mainColor: 'bg-gradient-to-br from-gray-500 to-gray-600',
  accentColor: 'bg-gray-500',
  textColor: 'text-gray-50',
  badgeBg: 'bg-gray-200',
  badgeText: 'text-gray-800',
  lightBg: 'bg-gray-50',
  borderColor: 'border-gray-300',
  icon: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-white">
      <path fillRule="evenodd" d="M2.25 4.5A.75.75 0 013 3.75h14.25a.75.75 0 010 1.5H3a.75.75 0 01-.75-.75zm0 4.5A.75.75 0 013 8.25h9.75a.75.75 0 010 1.5H3A.75.75 0 012.25 9zm15-.75A.75.75 0 0118 9v10.19l2.47-2.47a.75.75 0 111.06 1.06l-3.75 3.75a.75.75 0 01-1.06 0l-3.75-3.75a.75.75 0 111.06-1.06l2.47 2.47V9a.75.75 0 01.75-.75z" clipRule="evenodd" />
    </svg>
  )
};

export default function InfoCard({ itemData, onLoadCodeExample }) {
  if (!itemData) {
    return <div>No data provided for this card.</div>;
  }

  const {
    dataType,
    name,
    isMethod,
    description,
    example,
    parameters,
    optional,
    default: defaultValue,
    return_type
  } = itemData;

  // Get type details for the main data type
  const typeDetails = TYPES[itemData.datatype] || DEFAULT_TYPE;

  // Get type details for return type if it exists
  const returnTypeDetails = return_type ? (TYPES[return_type] || DEFAULT_TYPE) : null;

  // Function to extract code from example




  return (
    <div 
      className={`w-full flex flex-col rounded-lg overflow-hidden shadow-lg 
                  transition-all duration-300 hover:shadow-xl hover:translate-y-[-2px] 
                  bg-white dark:bg-gray-800 dark:text-gray-100 h-full border ${typeDetails.borderColor}`}
      data-datatype={itemData.datatype} id={itemData.name+itemData.datatype}
    >
      {/* Colorful Card Header */}
      <div className={`${typeDetails.mainColor} ${typeDetails.textColor} p-5`}>
        <div className="flex flex-col gap-3">
          {/* Method name and type */}
          <div className="flex gap-4">
            <h1 className="text-5xl font-bold tracking-wide">{name.split('(')[0]+"("}
              <span className="text-2xl ">{name.split('(')[1].replace(')', '')}</span>{")"}
            </h1>
          </div>
          
          {/* Badges row */}
          <div className="flex flex-wrap gap-4 mt-4">
            <div className="flex gap-2">
              <span className={`rounded-full text-3xl pl-1 pr-4 py-1 text-lg font-semibold ${typeDetails.badgeBg} ${typeDetails.badgeText} flex items-center gap-2`}>
                <div className={`${typeDetails.accentColor} rounded-full p-1.5 w-10 h-10 flex items-center justify-center`}>
                  {typeDetails.icon}
                </div>
                {itemData.datatype}
              </span>
            </div>
            {return_type && returnTypeDetails && (
              <span className={`rounded-full px-3 py-1 text-base font-semibold ${returnTypeDetails.badgeBg} ${returnTypeDetails.badgeText} flex items-center gap-1`}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6">
                  <path fillRule="evenodd" d="M12.207 2.232a.75.75 0 00.025 1.06l4.146 3.958H6.375a5.375 5.375 0 000 10.75H9.25a.75.75 0 000-1.5H6.375a3.875 3.875 0 010-7.75h10.003l-4.146 3.957a.75.75 0 001.036 1.085l5.5-5.25a.75.75 0 000-1.085l-5.5-5.25a.75.75 0 00-1.06.025z" clipRule="evenodd" />
                </svg>
                Devuelve: {return_type}
                <div className={`${returnTypeDetails.accentColor} rounded-full p-1.5 w-8 h-8 flex items-center justify-center`}>
                  {returnTypeDetails.icon}
                </div>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Card Description Area with Color Accent */}
      <div className={`${typeDetails.lightBg} dark:bg-gray-800/90 px-5 py-4 border-t border-b ${typeDetails.borderColor}`}>
        <p className="text-gray-700 dark:text-gray-300">{description}</p>
      </div>

      {/* Card Body */}
      <div className="p-5 flex-grow bg-white dark:bg-gray-800">
        {/* Code Example - BACK ON TOP */}
        {example && (
          <div className="mb-5">

            <SnippetArea example={example} />
          </div>
        )}

        {/* Parameters Section - NOW BACK BELOW THE SNIPPET */}
        {parameters && parameters.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className={`text-sm font-bold uppercase ${typeDetails.accentColor} text-white inline-block px-3 py-1 rounded-md`}>
                Parámetros
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                ({parameters.length} parámetro{parameters.length !== 1 ? 's' : ''})
              </span>
            </div>
            
            {/* Grid Layout for Parameters */}
            <div className="flex gap-4">
              {parameters.map((param, index) => (
                <div key={index} className={`border rounded-lg p-4 ${typeDetails.borderColor} bg-gray-50 dark:bg-gray-700/30 h-fit flex-1`}>
                  {/* Parameter Name and Required Status */}
                  <div className="flex flex-col gap-2 mb-3">
                    <code className={`text-lg font-mono font-bold px-3 py-1 rounded-md ${typeDetails.lightBg} ${typeDetails.badgeText} dark:bg-gray-600 dark:text-white border ${typeDetails.borderColor} text-center`}>
                      {param.name}
                    </code>
                    <span className={`text-sm px-2 py-1 rounded-full border self-center ${param.required ? 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300' : 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300'}`}>
                      {param.required ? 'Requerido' : 'Opcional'}
                    </span>
                  </div>
                  
                  {/* Parameter Description */}
                  <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed mb-4 font-medium">
                    {param.description}
                  </p>
                  
                  {/* Fancy Type Label */}
                  <div className="border-t pt-3 mt-auto">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        💡 Tipo:
                      </span>
                      <span className={`text-sm font-mono font-bold px-3 py-1 rounded-full text-white shadow-sm ${
                        Object.keys(TYPES).includes(param.type) 
                          ? TYPES[param.type].accentColor 
                          : 'bg-gray-600'
                      }`}>
                        {param.type}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Parameters Message */}
        {(!parameters || parameters.length === 0) && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <h3 className={`text-sm font-bold uppercase ${typeDetails.accentColor} text-white inline-block px-3 py-1 rounded-md`}>
                Parámetros
              </h3>
            </div>
            <div className={`border rounded-lg p-4 ${typeDetails.borderColor} bg-gray-50 dark:bg-gray-700/30 text-center`}>
              <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">Este método no requiere parámetros</span>
              </div>
            </div>
          </div>
        )}

        {/* Optional Info */}
        {optional && (
          <div className={`mt-4 text-sm px-3 py-2 rounded ${typeDetails.lightBg} ${typeDetails.badgeText} dark:bg-gray-700/50 dark:text-gray-300 border ${typeDetails.borderColor}`}>
            <span className="font-semibold">Optional</span>{defaultValue !== undefined ? ` (Default: ${defaultValue})` : ''}
          </div>
        )}
      </div>
    </div>
  );
} 